import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { identityEntityGetEntityOptions } from '@column/api';

import { CustomPageContext, PageTabs, useBreadcrumbTitle } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ApiError } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';

interface Params {
  id: string;
}

export const PageEntitySection: FC<CustomPageContext> = ({ entries }) => {
  const { id } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { currentUser } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();

  const { data: entity, error } = useQuery({
    ...identityEntityGetEntityOptions({ path: { entity_id: id } }),
    enabled: Boolean(id && currentUser),
    retry: (failureCount: number, queryError: unknown) => {
      const apiError = queryError as Partial<ApiError> | undefined;
      if (String(apiError?.code) === ApiErrCode.ResourceNotFound.toString()) {
        return false;
      }
      return failureCount < 3;
    },
  });

  useBreadcrumbTitle(entity?.name);

  useEffect(() => {
    if (!error) return;

    navigate(ROUTE.ENTITIES);
    addDangerNotification({
      content: (error as Error).message ?? 'Failed to load entity',
    });
  }, [error, navigate, addDangerNotification]);

  return (
    <>
      <PageTabs entries={entries} />
      {!error && <Outlet />}
    </>
  );
};
